// Interface registry.
//
// Vite's import.meta.glob picks up every sibling module at build time, so
// adding an interface means dropping a file in this directory -- no edit
// here, no registration list to forget. A module joins the registry by
// exporting `meta` and `mount`; see CONTRACT below.
//
// CONTRACT
//   export const meta = {
//     id: 'pads',            // stable, used in the URL and the CSV
//     label: 'Pads',         // shown on the mode switch
//     order: 10,             // sort position
//     paramGroup: 'pads',    // which SCHEMA group the panel shows with it
//     blurb: 'one line for the panel',
//   }
//
//     studyCondition: true,  // does the cued round run on this interface?
//   }
//
//   export function mount({ api, root, params, logger }) -> handle
//     `root` is an empty element owned by the interface for its lifetime. It is
//     NOT reused across mounts: expect it empty, and leave it empty.
//     Return a handle:
//       { unmount(),                    // required; remove every listener
//         study?: {
//           cue(sound) -> { down_before_cue? },   // present the cue on screen
//           clearCue(),
//           onOnset(cb) -> off,         // movement onset: cb({ t, ... })
//           onCancel(cb) -> off,        // input cancelled (iOS steals gestures)
//           setHome(on|null), hasHome,  // home position, study mode only
//           geometry(sound) -> {
//             pad_width_px?, pad_distance_px?,     // distance is home -> target
//             target_cx_px?, target_cy_px?,
//             home_cx_px?, home_cy_px?, has_home?,
//             region?,
//           },
//         } }
//     Interfaces must reach the audio graph only through `api`. Timing-relevant
//     facts ride to the log on the trigger's opts object -- see docs/timing.md.

// Eager, so the registry exists at module load with no async boot. The filter
// keeps helper modules (cli-parser.js, camera-tracker.js) out of the registry,
// but eager globbing would still BUNDLE them, tests included -- hence the
// explicit exclusion of test files from the glob itself.
const modules = import.meta.glob(['./*.js', '!./*.test.js', '!./index.js'], { eager: true })

export const INTERFACES = Object.entries(modules)
  .filter(([, mod]) => mod.meta && mod.mount)
  .map(([path, mod]) => ({ path, ...mod.meta, mount: mod.mount }))
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.id.localeCompare(b.id))

export function getInterface(id) {
  return INTERFACES.find((i) => i.id === id)
}

export const INTERFACE_IDS = INTERFACES.map((i) => i.id)

// Which interfaces the cued round runs on. The pattern view is a pads-style
// editor, not a selection task, so it is deliberately not a condition.
export const STUDY_INTERFACES = INTERFACES.filter((i) => i.studyCondition)
export const STUDY_INTERFACE_IDS = STUDY_INTERFACES.map((i) => i.id)
